import type { ChangeEvent } from 'react'
import { useMemo, useState } from 'react'

import { useDecision } from '@optimizely/react-sdk'
import Button from '@space-lib/components/button'
import Tooltip from '@space-lib/components/tooltip'
import { useDebounce } from '@space-lib/hooks/useDebounce'
import useTranslation from '@space-lib/hooks/useTranslation'
import ErrorBoundary from '@space-shell/common/components/errorBoundary'

import { useUserPermissions } from '@space-shell/common/hooks/useUserPermissions'
import { PermissionActions } from '@space-shell/common/services/communities'
import { useMembershipStore, useAuthStore, useUserInfoStore } from '@space-shell/core/store'
import { lazyRemote } from '@space-shell/lib/runtime'

import useCommunityConfigurationSidebar from '@space-shell/modules/communities/communityConfigurationSidebar/hooks/useConfigurationCommunitySidebar/useCommunityConfigurationSidebar'

import { ModerationFilters, ModerationTable, ModerationList } from './components'

import { SEARCH_DEBOUNCE_TIME } from './constants'
import { Box, Subheading, SubheadingDesc, Title } from './membersAndPermissionsStyles'
import { TabTypes, MemberTypes } from './types'

import { COMMUNITY_TYPE } from '../accessAndPrivacy/constants'
import useMembersImport from '../context/membersImportProvider/hook'

const MembersImport = lazyRemote('communityManagement/MembersImport')
const ImportHistory = lazyRemote('communityManagement/ImportHistory')
const MemberListSearchBar = lazyRemote('communityManagement/MemberListSearchBar')

const MembersAndPermissions = () => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { avatar, email } = useUserInfoStore()
  const { hasPermission } = useUserPermissions()
  const { membershipSlug } = useMembershipStore()
  const [page, setPage] = useState(TabTypes.FOLLOWER)
  const { community } = useCommunityConfigurationSidebar()
  const { isMembersImportOpen, setIsMembersImportOpen } = useMembersImport()
  const { locale, id, token, authorities, userName, ucode } = useAuthStore()
  const [{ enabled: isEnabledMembersImport }] = useDecision('com_adm_web_enable_members_import')
  const [{ enabled: isNewMembersListEnabled }] = useDecision('com_adm_web_enable_new_members_list')
  const [{ enabled: isEnabledMembersImportClassFilter }] = useDecision(
    'com_adm_web_enable_members_import_class_filter'
  )
  const [{ enabled: isEnabledMembersImportSelectAllGeneral }] = useDecision(
    'com_adm_web_enable_members_import_select_all_general'
  )
  const [{ enabled: isEnabledChangeCommunityType }] = useDecision(
    'com_adm_web_enable_change_community_type'
  )
  const shouldRenderMembersImport =
    hasPermission(PermissionActions.IMPORT_MEMBER, community?.permissions?.actions) &&
    isEnabledMembersImport &&
    community.type === COMMUNITY_TYPE.EXCLUSIVE &&
    page !== TabTypes.IMPORT_HISTORY

  const parentAppUser = useMemo(() => {
    return {
      id,
      token,
      authorities,
      name: userName,
      email: email ?? '',
      avatar,
      ucode
    }
  }, [authorities, avatar, email, id, token, ucode, userName])

  const debouncedSearch = useDebounce({
    value: search,
    delay: SEARCH_DEBOUNCE_TIME
  })

  const handleOnSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value)
  }

  const handleChangePage = (page: TabTypes) => {
    setSearch('')
    setPage(page)
  }

  const tab = useMemo(() => {
    const tabs = {
      [TabTypes.ADMIN]: isNewMembersListEnabled ? (
        <ModerationTable query={debouncedSearch} memberType={MemberTypes.ADMIN} />
      ) : (
        <ModerationList query={debouncedSearch} memberType={MemberTypes.ADMIN} />
      ),
      [TabTypes.FOLLOWER]: isNewMembersListEnabled ? (
        <ModerationTable query={debouncedSearch} memberType={MemberTypes.FOLLOWER} />
      ) : (
        <ModerationList query={debouncedSearch} memberType={MemberTypes.FOLLOWER} />
      ),
      [TabTypes.MODERATOR]: isNewMembersListEnabled ? (
        <ModerationTable query={debouncedSearch} memberType={MemberTypes.MODERATOR} />
      ) : (
        <ModerationList query={debouncedSearch} memberType={MemberTypes.MODERATOR} />
      ),
      [TabTypes.IMPORT_HISTORY]: (
        <ErrorBoundary>
          <ImportHistory
            setIsMembersImportOpen={setIsMembersImportOpen}
            community={{
              ...community,
              isLocked: isEnabledChangeCommunityType && community.isLocked
            }}
            parentAppUser={parentAppUser}
          />
        </ErrorBoundary>
      )
    }

    return tabs[page]
  }, [
    isNewMembersListEnabled,
    debouncedSearch,
    setIsMembersImportOpen,
    community,
    isEnabledChangeCommunityType,
    parentAppUser,
    page
  ])

  return (
    <Box>
      <Title>{t('configuration_sidebar.members_and_permissions.title_v2')}</Title>
      <Subheading>{t('configuration_sidebar.members_and_permissions.subheading')}</Subheading>
      <SubheadingDesc>
        {t('configuration_sidebar.members_and_permissions.subheading_desc')}
      </SubheadingDesc>
      {isMembersImportOpen ? (
        <ErrorBoundary>
          <MembersImport
            parentAppUser={parentAppUser}
            locale={locale}
            setPage={setPage}
            communityId={community.id}
            membershipSlug={membershipSlug}
            setIsMembersImportOpen={setIsMembersImportOpen}
            isEnabledMembersImportClassFilter={isEnabledMembersImportClassFilter}
            isEnabledMembersImportSelectAllGeneral={isEnabledMembersImportSelectAllGeneral}
          />
        </ErrorBoundary>
      ) : (
        <>
          <div className='_my-5'>
            <ModerationFilters onClick={handleChangePage} page={page} setPage={setPage} />
          </div>
          {shouldRenderMembersImport && (
            <div className='_d-flex _justify-content-end _mb-4'>
              <Tooltip
                content={t(
                  `${
                    community.isLocked
                      ? 'configuration_sidebar.change_community_type_tooltip_alert'
                      : ''
                  }`
                )}
                position='top'>
                <Button
                  type='button'
                  variation='secondary'
                  size='sm'
                  disabled={community.isLocked}
                  onClick={() => setIsMembersImportOpen(true)}>
                  {t('configuration_sidebar.members_and_permissions.import_members')}
                </Button>
              </Tooltip>
            </div>
          )}

          {page !== TabTypes.IMPORT_HISTORY && (
            <ErrorBoundary>
              <MemberListSearchBar
                placeholder={t('configuration_sidebar.members_and_permissions.search_label')}
                onChange={handleOnSearchChange}
              />
            </ErrorBoundary>
          )}
          {tab}
        </>
      )}
    </Box>
  )
}

export default MembersAndPermissions
